import React, { useState, useEffect } from "react";
import { MaterialReactTable } from 'material-react-table';
import { Box, Typography, Select, MenuItem } from '@mui/material';
import "./DataGrid.css";

const DataGrid = ({ rowData, columns, paginationConfig, rowExpandConfig, title, isLoading, enableRowSelection, setIsSelectedRow, className, globalSearchConfig, enableTopToolbar, filterConfig, enableFullScreenToggle, enableDensityToggle, enableHiding }) => {
    const root = document.documentElement;
    const MuiTableColor = getComputedStyle(root).getPropertyValue('--tableHeadRow-background-color').trim();
    const MuiTableTextColor = getComputedStyle(root).getPropertyValue('--tableHeadRow-font-color').trim();
    const [rowSelection, setRowSelection] = useState({});
    const [gridSearchInput, setGridSearchInput] = useState('');
    const handleRowSelectionChange = (rowSelection) => {
        setRowSelection(rowSelection)
    };
    const defaultExpandedTitles = ["Ageing Report", "Inventory Report"]

    const handleSearchInput = (input) => {
        setGridSearchInput(input)
        if (!input ) {
            globalSearchConfig.handleClearSearch()
        }
    }
    const handleKeyDown = (e) => {
        if (e.key === "Enter") {
            const inputValue = e.target.value; 
            globalSearchConfig.handleGlobalSearch(inputValue);
            paginationConfig.setPagination({
                pageIndex: 0,
                pageSize: paginationConfig.pagination.pageSize,
            });
        }
    }
    useEffect(() => {
        const getSelectedIds = Object.keys(rowSelection)
        if (setIsSelectedRow) {
            setIsSelectedRow(getSelectedIds)
        }
    }, [rowSelection, setIsSelectedRow])
    return (

        <>
            <MaterialReactTable
                manualPagination
                columns={columns}
                data={rowData}
                layoutMode="grid" // ✅ Force flex-based layout
                enableColumnResizing
                columnResizeMode="onEnd"
                enableStickyHeader
                rowCount={paginationConfig.totalrecords}
                enableRowSelection={enableRowSelection}
                getRowId={(originalRow) => originalRow.id}
                enableSelectAll={false}
                onRowSelectionChange={handleRowSelectionChange}
                enableRowExpansion={rowExpandConfig?.enableRowExpansion}
                enableExpandAll={false}
                enableTopToolbar={enableTopToolbar}
                enableColumnPinning={true}
                muiColumnActionsButtonProps={{
                  sx: {
                    display: 'none', // hides the column action menu button
                  },
                }}
                state={{
                    pagination: paginationConfig.pagination,
                    showGlobalFilter: true,
                    isLoading,
                    columnPinning: { right: ['actions', 'action']},
                    rowSelection,
                    gridSearchInput,
                    density : 'comfortable',
                    showSkeletons: isLoading ? true : false,
                }}
                // defaultColumn={{ size: 1, minSize: 100, maxSize: Number.MAX_SAFE_INTEGER }}
                // muiTableBodyCellProps={{
                //     sx: {
                //         fontSize: '1.3rem',
                //         textAlign: 'left',
                //         whiteSpace: 'normal',
                //         wordBreak: 'break-word',
                //         maxWidth: '100%', // optional
                //     },
                // }}
                // muiTableHeadCellProps={{
                //     sx: {
                //         fontSize: '1.3rem',
                //         fontFamily: 'Poppins, sans-serif',
                //         whiteSpace: 'nowrap',
                //         overflowX: 'hidden',
                //         textOverflow: 'ellipsis',
                //         textAlign: 'left',
                //     },
                // }}
                muiTopToolbarProps={{
                    sx: {
                        minHeight: '4.5rem',
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',

                    }
                }}
                muiTableContainerProps={{ 
                    sx: { 
                    minHeight: "70vh",
                    width: "100%",
                    overflowX: "auto",
                 },
                  className: className }}
                onPaginationChange = {(newPagination) => {
                    paginationConfig.setPagination(newPagination)
                }}
                muiTablePaginationProps={{
                    rowsPerPageOptions: [],
                }}
                renderDetailPanel={(rowExpandConfig?.enableRowExpansion) ? 
                    ({ row }) => ((row.original.childData?.length > 0 || (row.original.parentData?.data.length > 0 || defaultExpandedTitles.includes(title))) ? 
                    rowExpandConfig?.expandRow?.renderer(row) : null) 
                    : null}
                muiCircularProgressProps={{
                    color: 'primary',
                    thickness: 5,
                    size: 55,
                }}
                muiSkeletonProps={{
                    animation: 'pulse',
                    height: 28,
                }}

                enableGlobalFilter={globalSearchConfig?.enableGlobalFilter}
                globalFilterFn="contains"
                enableSearchToggle={false} 
                enableColumnFilters={false}
                enableFullScreenToggle={enableFullScreenToggle}
                enableDensityToggle= {false}
                enableHiding={false}
                enablePagination={paginationConfig?.enablePagination}

                {...(globalSearchConfig?.enableGlobalFilterModes ? {} : { onGlobalFilterChange: handleSearchInput })}
                positionGlobalFilter="left"
                muiSearchTextFieldProps={{
                    placeholder: `Search ${globalSearchConfig?.placeholder ? globalSearchConfig?.placeholder : ""}`,
                    ...(globalSearchConfig?.enableGlobalFilterModes ? {} : { onKeyDown: (e) => handleKeyDown(e) }),
                    sx: {minWidth: '300px'},
                    variant: 'outlined',
                }}
                paginationDisplayMode='pages'
                muiPaginationProps={{
                    showRowsPerPage: false,
                    variant: 'outlined',
                    sx: {
                        '& .MuiPaginationItem-root.Mui-selected': {
                            backgroundColor: MuiTableColor,
                            color: MuiTableTextColor,
                        }
                    }
                }
                }
                muiBottomToolbarProps={{
                    sx: {
                        minHeight: '5rem'
                    }
                }}

                renderTopToolbarCustomActions={() => (
                    <h4>{title === "Ageing Report" || title === "Bin Location" ? title : ""}</h4>
                )}
                renderBottomToolbarCustomActions={({ table }) => {
                  const rowCount = table.getRowModel().rows.length;
                  if (rowCount === 0) return null;
                  return (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      gap: 2,
                      padding: '0.5rem 1rem',
                    }}
                  >
                    {/* Page No Dropdown */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2"
                      sx={{
                        fontSize: '12px',
                        fontWeight: 500,
                        fontFamily: 'Poppins, sans-serif',
                        textTransform: 'capitalize',
                      }}>Page No:</Typography>
                      <Select
                        value={table.getState().pagination.pageIndex}
                        onChange={(e) => table.setPageIndex(Number(e.target.value))}
                        size="small"
                        sx={{ minWidth: 60 }}
                      >
                        {Array.from(
                          { length: table.getPageCount() }, (_, i) => (
                          <MenuItem key={i} value={i}>
                            {i + 1}
                          </MenuItem>
                        )
                        )}
                      </Select>
                    </Box>
                
                    {/* Rows per page dropdown */}
                    {/* <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2">Rows per page:</Typography>
                      <Select
                        value={table.getState().pagination.pageSize}
                        onChange={(e) => table.setPageSize(Number(e.target.value))}
                        size="small"
                        sx={{ minWidth: 80 }}
                      >
                        {[5, 10, 25, 50, 100].map((size) => (
                          <MenuItem key={size} value={size}>
                            {size}
                          </MenuItem>
                        ))}
                      </Select>
                    </Box> */}
                  </Box>
                  )
                }}
                
                muiTableHeadRowProps={{
                     sx: {
                        'th[data-pinned="true"]::before': { backgroundColor: MuiTableColor },
                         fontWeight: 'bold',
                         fontFamily: 'Poppins, sans-serif',
                         '& .MuiTableCell-root': {
                             backgroundColor: MuiTableColor,
                             color: MuiTableTextColor
                        },
                      },
                  }}
                displayColumnDefOptions={{
                    'mrt-row-expand': {
                        size: 60,
                        minSize: 30,
                        maxSize: 90,
                        header: "",
                    },
                    '& .MuiTableSortLabel-root': {
                      marginLeft: 'auto',
                    },
                    '& .Mui-TableHeadCell-ResizeHandle-Wrapper': {
                      marginRight: '8px',
                      position: 'static'
                    },
                    "mrt-row-select": {
                        header: "",
                    },
                }}

                enableColumnActions={false}
            
            />
        </>

    );
}

export default DataGrid;
